import { useGetApPayeeInfoSuspense } from '@/context/services/ap_payee/ApPayee.service';
import {
  isAuthError,
  isBadRequestError,
  isForbiddenError,
  isPaymentRequiredError,
} from '@/context/services/type';
import { PAYEE_LIST_ERROR_TOAST_MESSAGE_KEY } from '@/features/ap_payees/components/PayeeDetailDrawer/constants';
import {
  GetMasterData,
  GetNullDataMaster,
  navigateToListWithMessage,
  navigateToPayeeList,
} from '@/features/ap_payees/components/PayeeDetailDrawer/utils';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useEffect, useMemo } from 'react';
import { DataMaster } from '../types';
import { getErrorMessage } from '../utils';

export const useGetApPayeeInfo = (apPayeeId: string, onClose?: () => void) => {
  const { t } = useTranslation();
  const notify = useToastAPI();
  const { statusCode, data, error, isLoading, refetch } =
    useGetApPayeeInfoSuspense(apPayeeId);
  const dataValue: DataMaster | undefined = useMemo(() => {
    if (error !== undefined) {
      return undefined;
    }

    return data ? GetMasterData(data) : GetNullDataMaster();
  }, [data, error]);

  useEffect(() => {
    if (error !== undefined) {
      const errorMessage = getErrorMessage({
        t,
        statusCode,
        errorMessages: error.messages,
        defaultKey: 'ap_payees_detail_drawer.error.not_found',
      });

      if (isBadRequestError(statusCode) || isPaymentRequiredError(statusCode)) {
        notify.error({
          title: errorMessage,
          duration: 5,
        });
        onClose?.();
        return;
      }

      if (
        isAuthError(statusCode) ||
        (isForbiddenError(statusCode) && error.code === 'no_role')
      ) {
        navigateToPayeeList(onClose);
        return;
      }

      navigateToListWithMessage(
        errorMessage,
        PAYEE_LIST_ERROR_TOAST_MESSAGE_KEY,
        onClose
      );
    }
  }, [error, notify, t, onClose, statusCode]);

  return {
    data: dataValue,
    error,
    isLoading,
    refetch,
  };
};
